/**
 * AI 实时辅助通话入口
 *
 * 整体受权限点 tcc.permission.calling.feature-r（authPointMap.callingFeatureGray）控制：
 * 无权限时呼叫提示、入口条、抽屉一律不出现，也不监听 TCCC 浮层。
 *
 * 通话中在页面右侧居中展示圆弧电话提示（CallingIndicator），点击弹出 AI 智能通话抽屉。
 *
 * customerId 来源：当前所在的客户详情页 /customer/:customerId。
 * 仅当本通电话就是在「当前客户详情页 + 本标签页」呼出时才展示提示与抽屉：
 * 通话中切到别的客户页、工作台等无客户上下文页面呼出的电话、
 * 以及同一坐席的其他标签页（SDK 会把通话状态广播到所有 tab），都不展示，
 * 避免拿错客户的信息去呈现这通电话、或在非通话 tab 弹出抽屉。
 */
import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import { useLocation, useRouteMatch } from 'react-router-dom'
import { globalStore, t3cStore } from '@store'
import { customerDetailStore } from '@pages/customer/detail/store'
import type { CustomerInfo } from '@types'
import AiCallingDrawer from './AiCallingDrawer'
import CallingIndicator from './CallingIndicator'
import { useTcccCallBarRect } from './useTcccCallBarRect'
import './style.less'

/**
 * 挂在 T3C 外呼工作台浮层下方的「AI实时辅助通话」入口条开关。
 *
 * 当前置 false：通话中已由页面右侧的圆弧电话提示（CallingIndicator）引导打开 AI 抽屉，
 * 浮层下方再挂一条入口重复且会遮挡浮层，先隐藏；需要恢复只需改回 true。
 * 置 false 时同时停掉 TCCC 浮层位置监听（每帧穿透 Shadow DOM 遍历，开销较大）。
 */
const ENTRY_BAR_ENABLED = false

/** 入口条与浮层底部的间距（px），负值让入口条上移遮住浮层底部圆角，实现无缝衔接 */
const GAP = -2
/** 入口条高度（px） */
const ENTRY_HEIGHT = 40

/** AI 实时辅助通话页路径前缀 */
const AI_CALLING_PATH_PREFIX = '/ai-calling'
/** 客户详情页路径（customerId 来源） */
const CUSTOMER_DETAIL_PATH = '/customer/:customerId'

const AiCallingEntry: React.FC = () => {
  const location = useLocation()
  // AI 实时通话辅助功能权限点 tcc.permission.calling.feature-r：
  // 无权限则本组件整体不生效 —— 右侧呼叫提示（圆弧电话按钮）、外呼浮层下方入口条、
  // AI 智能通话抽屉都不渲染，浮层监听也不启动（权限未拉取到时同样按无权限处理）。
  const hasGrayPermission = globalStore(s => s.authPointMap.callingFeatureGray?.result || false)
  const rect = useTcccCallBarRect(ENTRY_BAR_ENABLED && hasGrayPermission)
  // 从当前客户详情页路由中解析 customerId（工作台等无客户上下文的页面解析不到，为 undefined）
  const match = useRouteMatch<{ customerId: string }>(CUSTOMER_DETAIL_PATH)
  const customerId = match?.params.customerId
  // 客户详情 store 只在客户页有值，导航离开后会被清空/切换。
  // 这里按「当前路由客户」取，下面再按通话归属客户缓存一份，供抽屉常驻期间使用。
  const routeCustomerInfo = customerDetailStore(s => s.customerInfo)

  const drawer = globalStore(s => s.aiCallingDrawer)
  const setAiCallingDrawer = globalStore(s => s.setAiCallingDrawer)
  const drawerCustomerId = drawer?.customerId
  const drawerOpen = drawer?.open || false

  // ===== 本通电话的归属判定 =====
  // 判定只在「idle → 通话中」的跃变时做一次并锁定，之后不再随状态变化重算，原因：
  //   1. 挂断后 resetCallState 会清空 sessionId，若实时重算会立刻判为「非本通」，
  //      右侧提示被瞬间卸载，放不完「未查看 → 变红 → 渐出」的收尾动画；
  //   2. 通话中切到别的客户页时不应重算（否则会把新客户当成呼出页）。
  // 结果保留到下一通电话覆盖。
  const callStatus = t3cStore(s => s.callStatus)
  const sessionId = t3cStore(s => s.sessionId)
  const callActive = callStatus === 'calling' || callStatus === 'talking'

  // 呼出页客户 id 用 ref 承载，避免把 customerId 加进下面 effect 的依赖
  // （否则通话中切换客户会重算归属，正是要防的场景）。在 effect 中更新而非渲染期赋值，
  // 且本 effect 声明在前、切换客户时先于归属 effect 执行，故读到的总是最新值。
  const customerIdRef = useRef(customerId)
  useEffect(() => {
    customerIdRef.current = customerId
  }, [customerId])

  const [callOrigin, setCallOrigin] = useState<{ customerId?: string; ownedByThisTab: boolean }>()
  useEffect(() => {
    // 挂断时（callActive 为 false）直接返回，保留上一次判定结果，让收尾动画播完
    if (!callActive) return
    // 依赖里带上 sessionId：若上一通未经 idle 就直接进入下一通
    //（callStatus 由 talking 直接变 calling，callActive 始终为 true），
    // 仅靠 callActive 跃变不会重算，会沿用上一通的归属。sessionId 变化可覆盖这种情况。
    // calling → talking 时 sessionId 不变，不会重复重算。
    //
    // 在 effect 中读取 store 快照，而不是在 t3cStore.subscribe 回调里判定：
    // callOuted 内部按 setCallStatus → setSessionId → setCallOwner 顺序连续多次 set，
    // subscribe 是同步逐次触发的，首次触发时 sessionId / callOwner 还是上一通的旧值。
    // effect 在 React 批处理后执行，此时三者均为最终值。
    const { sessionId: sid, callOwner, curTabUUID } = t3cStore.getState()
    // 多标签页隔离：SDK 把通话事件广播到同一坐席的所有标签页，各 tab 的 callStatus /
    // sessionId 完全一致，只有事件里的 tabUUID 能区分「这通电话在哪个 tab 打的」
    //（SDK 给标签页 favicon 打红点、来电表单只在接入 tab 弹出，用的都是它）。
    //   - callOwner.sessionId 必须与当前通话一致：不一致说明本 tab 没收到这通电话的事件
    //     （通话中途才新开/刷新的标签页即是，其 callOwner 为 null 或仍是上一通），判为非本 tab；
    //   - 一致时再比 tabUUID；为空表示 SDK 未下发该字段，降级为不做 tab 区分，
    //     避免整个功能失效（此时仍有呼出页归属兜底）。
    const ownedByThisTab = !!sid
      && callOwner?.sessionId === sid
      && (!callOwner.tabUUID || !curTabUUID || callOwner.tabUUID === curTabUUID)
    setCallOrigin({ customerId: customerIdRef.current, ownedByThisTab })
  }, [callActive, sessionId])

  // 本通电话归属的客户 id。通话期间固定为「呼出时所在的客户页」，不随后续路由变化，
  // 使 AI 通话内容在坐席导航到其他页面时仍保持挂载（WS 与核验状态不中断）。
  const callCustomerId = callOrigin?.ownedByThisTab ? callOrigin.customerId : undefined

  // 仅当本通电话在本标签页呼出、且呼出页是客户详情页时，才启用 AI 通话相关内容。
  // 工作台等无客户上下文页面呼出的电话不展示（无法关联客户信息）。
  const aiCallingEnabled = !!callCustomerId

  // 右侧圆弧提示只在「呼出的那个客户页」展示，避免在其他页面打扰坐席；
  // 而抽屉一旦打开就保持挂载（见下方），与当前路由无关。
  const showIndicator = aiCallingEnabled && callCustomerId === customerId

  // 缓存通话客户的详情：抽屉常驻期间坐席可能已导航到别的页面，
  // 此时 customerDetailStore 里已是别的客户（或为空），不能再实时读取。
  const [callCustomerInfo, setCallCustomerInfo] = useState<CustomerInfo>()
  useEffect(() => {
    if (!callCustomerId) {
      setCallCustomerInfo(undefined)
      return
    }
    // 仅在 store 中恰好是通话客户时更新缓存；导航走后保留最后一次拿到的值
    if (routeCustomerInfo?.customerId === Number(callCustomerId)) {
      setCallCustomerInfo(routeCustomerInfo)
    }
  }, [callCustomerId, routeCustomerInfo])

  // 权限被回收（权限每小时刷新一次）时清空抽屉状态。
  // 注意不要依赖路由 customerId：通话中导航到其他页面不应关闭抽屉，
  // 抽屉的生命周期由通话本身决定（见下方 callCustomerId 的清理）。
  useEffect(() => {
    if (!hasGrayPermission) setAiCallingDrawer(null)
  }, [hasGrayPermission, setAiCallingDrawer])

  // 通话归属客户变化时重置抽屉状态，避免上一通的抽屉残留到下一通。
  // 用 ref 跳过首次执行：挂载时 callCustomerId 必为 undefined，
  // 若此时也清空，会把「本 tab 之外写入的抽屉状态」误清掉。
  const prevCallCustomerIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevCallCustomerIdRef.current !== callCustomerId) {
      prevCallCustomerIdRef.current = callCustomerId
      // 仅在切到新的通话客户时重置；归属清空（挂断后）不动抽屉，
      // 让坐席挂断后仍能回看本通内容，直到其手动关闭。
      if (callCustomerId) setAiCallingDrawer(null)
    }
  }, [callCustomerId, setAiCallingDrawer])

  // 无权限：整体不生效。
  // 注意这里不再因「当前路由是 /ai-calling」而提前 return —— 那样会在坐席通话中
  // 打开独立辅助页时卸载抽屉、断开 WS。该场景改为下方只隐藏 UI，内容仍保持挂载。
  if (!hasGrayPermission) return null

  // 位于 /ai-calling 独立辅助页时，页面自身已提供完整 AI 通话界面，
  // 此处不再重复展示提示与抽屉 UI（抽屉通过 open=false 隐藏，内容不卸载）。
  const onAiCallingPage = location.pathname.startsWith(AI_CALLING_PATH_PREFIX)

  const style: React.CSSProperties | undefined = rect ? {
    position: 'fixed',
    top: rect.top + rect.height + GAP,
    left: rect.left,
    width: rect.width,
    height: ENTRY_HEIGHT,
  } : undefined

  return (
    <>
      {!onAiCallingPage && showIndicator && callCustomerId && (
        <CallingIndicator
          key={`indicator-${callCustomerId}`}
          customerId={callCustomerId}
          drawerOpen={drawerOpen && drawerCustomerId === callCustomerId}
        />
      )}
      {!onAiCallingPage && ENTRY_BAR_ENABLED && rect && !drawerOpen && (
        <Button
          className="ai-calling-entry"
          href={customerId ? undefined : AI_CALLING_PATH_PREFIX}
          style={style}
          target={customerId ? undefined : '_blank'}
          rel={customerId ? undefined : 'noopener noreferrer'}
          onClick={customerId ? () => setAiCallingDrawer({ customerId, open: true }) : undefined}
          aria-label="进入 AI 实时辅助通话"
        >
          <span className="ai-calling-entry__icon" aria-hidden="true">✦</span>
          <span className="ai-calling-entry__text">AI实时辅助通话</span>
          <span className="ai-calling-entry__arrow" aria-hidden="true">›</span>
        </Button>
      )}
      {/* 抽屉挂载只取决于「本通电话的归属客户」，与当前路由无关：
          坐席通话中导航到工作台等页面时，抽屉内容（WS 连接、已核验的产品名替换、
          异议/交叉销售去重集合）必须继续存活，否则会触发 AiCallingContent 卸载，
          进而发出 clear 帧断开 WS、丢失本通已识别的产品名。
          key 用 callCustomerId：仅在换客户（即换一通电话）时才重建。 */}
      {aiCallingEnabled && callCustomerId && (
        <AiCallingDrawer
          key={callCustomerId}
          customerId={callCustomerId}
          customerInfo={callCustomerInfo}
          open={!onAiCallingPage && drawerOpen && drawerCustomerId === callCustomerId}
          onClose={() => setAiCallingDrawer({ customerId: callCustomerId, open: false })}
        />
      )}
    </>
  )
}

export default AiCallingEntry
